import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "../../utils/toast";
import WorkWithUs from "../../components/sections/WorkWithUs";

const OTPVerification = ({ title, buttonText, redirectPath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [phone, setPhone] = useState("");
  const [requiresProfileCompletion, setRequiresProfileCompletion] = useState(
    false,
  );
  const [completeLoading, setCompleteLoading] = useState(false);
  const [pendingRegistrationToken, setPendingRegistrationToken] = useState("");
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    is_adult_confirmed: true,
  });

  const inputsRef = useRef([]);

  useEffect(() => {
    if (location.state && location.state.phone) {
      setPhone(location.state.phone);
      if (
        location.state.is_new_user === true &&
        location.state.verifiedToken
      ) {
        setPendingRegistrationToken(location.state.verifiedToken);
        setRequiresProfileCompletion(true);
      }
    } else {
      toast.error("Phone number missing. Redirecting to login.");
      setTimeout(() => navigate("/login"), 3000);
    }
  }, [location.state, navigate]);

  const handlePostLoginRedirect = () => {
    window.dispatchEvent(new Event("loginStateChange"));
    const pendingBooking = localStorage.getItem("pendingBooking");
    const from = (location.state && location.state.from) || redirectPath;

    setTimeout(() => {
      if (pendingBooking) {
        navigate(from || "/booking/airport");
      } else {
        navigate(from);
      }
    }, 1500);
  };

  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, "");
    if (!value) {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input
    if (index < 3) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalOtp = otp.join("");

    if (finalOtp.length < 4) {
      toast.error("Please enter complete 4-digit OTP");
      return;
    }

    if (!phone) {
      toast.error("Phone number is missing.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/verify-otp`,
        { phone, otp: finalOtp },
      );

      if (response.data.status === true) {
        toast.success(response.data.message || "Verified successfully!");

        if (response.data.is_new_user === true) {
          setPendingRegistrationToken(response.data.token || "");
          setRequiresProfileCompletion(true);
          return;
        }

        // Save token and user data only for existing users.
        localStorage.setItem("user_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.customer));
        localStorage.setItem(
          "user_data",
          JSON.stringify(response.data.customer),
        );
        handlePostLoginRedirect();
      } else {
        toast.error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          toast.error(errors[key][0]);
        });
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    if (!profileData.first_name.trim() || !profileData.last_name.trim()) {
      toast.error("Please enter first name and last name");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(profileData.first_name.trim())) {
      toast.error("First name may only contain letters.");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(profileData.last_name.trim())) {
      toast.error("Last name may only contain letters.");
      return;
    }
    const email = profileData.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!profileData.is_adult_confirmed) {
      toast.error("You must be at least 18 years old to continue.");
      return;
    }

    const token = pendingRegistrationToken;
    if (!token) {
      toast.error("Session expired. Please login again.");
      navigate("/login");
      return;
    }

    setCompleteLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/complete-registration`,
        {
          first_name: profileData.first_name.trim(),
          last_name: profileData.last_name.trim(),
          email: email || null,
          is_adult_confirmed: profileData.is_adult_confirmed ? 1 : 0,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === true) {
        localStorage.setItem("user_token", token);
        localStorage.setItem("user", JSON.stringify(response.data.customer));
        localStorage.setItem("user_data", JSON.stringify(response.data.customer));
        setPendingRegistrationToken("");
        toast.success(response.data.message || "Registration completed!");
        handlePostLoginRedirect();
      } else {
        toast.error(response.data.message || "Failed to complete registration");
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          toast.error(errors[key][0]);
        });
      } else {
        toast.error(
          error?.response?.data?.message ||
          "Failed to complete registration. Please try again.",
        );
      }
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    if (!phone) return;

    setResending(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/resend-otp`,
        { phone },
      );

      if (response.data.status === true) {
        toast.success(response.data.message || "OTP resent successfully!");
      } else {
        toast.error(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Server error while resending OTP.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <section className="login_wrap">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="login_formMain">
                <div className="loginForm_inner">
                  {/* <h2>{title}</h2> */}

                  {!requiresProfileCompletion ? (
                    <>
                      <h2 className="mb-4 text-center">Enter OTP</h2>
                      <div className="form-group mb-4">
                        <label
                          className="d-flex align-items-center justify-content-center flex-wrap w-100"
                          style={{ textAlign: "center" }}
                        >
                          An OTP was sent to +91 {phone || "XXXXXXX"}
                          <Link
                            to="/login"
                            className="ml-2"
                            style={{ color: "#ffc107", marginLeft: "5px" }}
                          >
                            Edit
                          </Link>
                        </label>

                        <div className="inputs inputs_otp d-flex flex-row justify-content-center mt-2">
                          {otp.map((data, index) => (
                            <input
                              key={index}
                              type="text"
                              maxLength="1"
                              className="text-center form-control rounded mx-1"
                              value={data}
                              ref={(el) => (inputsRef.current[index] = el)}
                              onChange={(e) => handleChange(e.target, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              autoFocus={index === 0}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="new_user mb-4">
                        <p>
                          <a
                            href="#!"
                            onClick={handleResendOTP}
                            style={{
                              pointerEvents: resending ? "none" : "auto",
                              color: resending ? "#999" : "",
                            }}
                          >
                            {resending ? "Resending..." : "Resend OTP"}
                          </a>
                        </p>
                      </div>

                      <div className="login_btn mb-4">
                        <button
                          className="btn"
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? "Verifying..." : buttonText}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="mb-4 text-center">Complete Registration</h2>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label>First Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={profileData.first_name}
                            onChange={(e) =>
                              setProfileData((prev) => ({
                                ...prev,
                                first_name: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <label>Last Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={profileData.last_name}
                            onChange={(e) =>
                              setProfileData((prev) => ({
                                ...prev,
                                last_name: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <label>Email</label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Optional"
                            value={profileData.email}
                            onChange={(e) =>
                              setProfileData((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-check d-flex align-items-center">
                            <input
                              type="checkbox"
                              id="is_adult_confirmed"
                              className="form-check-input"
                              style={{ width: "14px", height: "14px" }}
                              checked={profileData.is_adult_confirmed}
                              onChange={(e) =>
                                setProfileData((prev) => ({
                                  ...prev,
                                  is_adult_confirmed: e.target.checked,
                                }))
                              }
                            />
                            <label
                              htmlFor="is_adult_confirmed"
                              className="ms-2 mb-0"
                            >
                              I am 18+ years old
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="login_btn mb-4">
                        <button
                          className="btn"
                          onClick={handleCompleteRegistration}
                          disabled={completeLoading}
                        >
                          {completeLoading
                            ? "Completing..."
                            : "Complete Registration"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <WorkWithUs />
      <ToastContainer />
    </>
  );
};

export default OTPVerification;
